import { useState } from 'react';
import {
    Typography, Box, Button, Chip, Stack, Paper, Avatar,
    Link, CircularProgress, Alert, Card, CardContent, Container
} from '@mui/material';
import {
    Edit, ArrowBack, Link as LinkIcon, Person,
    Category, LocationOn, AttachMoney, Description, Instagram,
    Facebook, YouTube, Twitter, LinkedIn
} from '@mui/icons-material';
import CreatorProfileForm from './CreatorProfileForm';
import useBrandProfile from '../../Hooks/useBrandProfile';
import ProfileSection from '../../components/ProfileSection';
import resolveImageUrl from '../../utils/resolveImageUrl';
import { brandGradient } from '../../shared-theme/themePrimitives';
import { SENTIMENT_COLORS, sentimentLabel } from '../../utils/sentiment';

const getPlatformIcon = (platformName) => {
    const iconMap = {
        'Instagram': <Instagram fontSize="small" />,
        'Facebook': <Facebook fontSize="small" />,
        'YouTube': <YouTube fontSize="small" />,
        'Twitter': <Twitter fontSize="small" />,
        'LinkedIn': <LinkedIn fontSize="small" />,
        'TikTok': <YouTube fontSize="small" />,
    };
    return iconMap[platformName] || <LinkIcon fontSize="small" />;
};

export default function CreatorProfilePage() {
    const { profile, loading, error, fetchProfile } = useBrandProfile();
    const [editing, setEditing] = useState(false);

    const handleSave = async () => {
        await fetchProfile();
        setEditing(false);
    };

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancel = () => {
        setEditing(false);
    };

    // Show loading state
    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', my: 4 }}>
                <Alert severity="error">
                    Failed to load creator profile. Please try again.
                </Alert>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button onClick={fetchProfile} variant="contained">
                        Retry
                    </Button>
                </Box>
            </Box>
        );
    }

    if (editing || !profile) {
        return (
            <Box sx={{ position: 'relative' }}>
                {profile && (
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={handleCancel}
                        color="secondary"
                        sx={{ position: 'absolute', left: 8, top: 8, zIndex: 1 }}
                    >
                        Cancel
                    </Button>
                )}
                <CreatorProfileForm
                    onSave={handleSave}
                    defaultValues={profile}
                    isEditing={!!profile}
                />
            </Box>
        );
    }

    // Format address
    const formatAddress = () => {
        const parts = [
            profile?.address?.street,
            profile?.address?.city,
            profile?.address?.state,
            profile?.address?.zip,
            profile?.address?.country
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'Not specified';
    };

    const avatarUrl = resolveImageUrl(profile?.creator?.profile_image);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                {/* Cover + identity */}
                <Box sx={{
                    height: { xs: 100, sm: 140 },
                    background: avatarUrl
                        ? `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.35)), url(${avatarUrl})`
                        : brandGradient,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-end' },
                    gap: 2,
                    px: 3,
                    pb: 3,
                    mt: '-48px',
                    textAlign: { xs: 'center', sm: 'left' },
                }}>
                    <Avatar
                        src={avatarUrl || undefined}
                        sx={{
                            width: 96,
                            height: 96,
                            border: '4px solid',
                            borderColor: 'background.paper',
                            boxShadow: 2,
                            fontSize: '2rem',
                            fontWeight: 700,
                            bgcolor: 'secondary.main',
                        }}
                    >
                        {profile?.creator?.bio?.charAt(0)?.toUpperCase() || 'C'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" fontWeight={700}>Creator Profile</Typography>
                        {profile.gender && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Person fontSize="small" /> {profile.gender}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Edit />}
                        onClick={handleEdit}
                        sx={{ borderRadius: '999px', px: 3, flexShrink: 0 }}
                    >
                        Edit Profile
                    </Button>
                </Box>

                <Box sx={{ px: 3, pb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ProfileSection icon={<Description fontSize="small" />} title="Bio">
                                <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                    {profile?.creator?.bio || 'No bio provided'}
                                </Typography>
                            </ProfileSection>

                            {profile.description && (
                                <ProfileSection icon={<Description fontSize="small" />} title="Additional Description">
                                    <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                        {profile.description}
                                    </Typography>
                                </ProfileSection>
                            )}

                            <ProfileSection icon={<Category fontSize="small" />} title="Content Categories">
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {profile?.creator?.content_category && profile?.creator?.content_category.length > 0 ? (
                                        profile?.creator?.content_category.map(cat => (
                                            <Chip key={cat} label={cat} color="secondary" variant="outlined" />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary">No content categories specified</Typography>
                                    )}
                                </Stack>
                            </ProfileSection>

                            <ProfileSection icon={<LocationOn fontSize="small" />} title="Location" sx={{ mb: 0 }}>
                                <Typography sx={{ lineHeight: 1.6 }}>
                                    {formatAddress()}
                                </Typography>
                            </ProfileSection>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ProfileSection icon={<LinkIcon fontSize="small" />} title="Social Media Platforms" sx={{ mb: 0 }}>
                                {profile.platforms && profile.platforms.length > 0 ? (
                                    <Stack spacing={2}>
                                        {profile.platforms.map((platform, index) => (
                                            <Card key={index} variant="outlined" sx={{ borderRadius: 2 }}>
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        {getPlatformIcon(platform.name)}
                                                        <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700, flexGrow: 1 }}>
                                                            {platform.name}
                                                        </Typography>
                                                        {sentimentLabel(platform.sentiment_label) && (
                                                            <Chip
                                                                label={sentimentLabel(platform.sentiment_label)}
                                                                size="small"
                                                                color={SENTIMENT_COLORS[platform.sentiment_label] || 'default'}
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>

                                                    {platform.profile_url && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Link
                                                                href={platform.profile_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                color="secondary.main"
                                                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                            >
                                                                {platform.profile_url.replace(/(^\w+:|^)\/\//, '')}
                                                            </Link>
                                                        </Box>
                                                    )}

                                                    {platform.genre && platform.genre.length > 0 && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                Genres:
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                                {platform.genre.map(genre => (
                                                                    <Chip key={genre} label={genre} size="small" color="secondary" variant="outlined" />
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}

                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                        {platform.reel_price > 0 && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AttachMoney fontSize="small" color="secondary" />
                                                                <Typography variant="body2">
                                                                    Reel: ${platform.reel_price}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {platform.story_price > 0 && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AttachMoney fontSize="small" color="secondary" />
                                                                <Typography variant="body2">
                                                                    Story: ${platform.story_price}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {platform.live_price > 0 && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AttachMoney fontSize="small" color="secondary" />
                                                                <Typography variant="body2">
                                                                    Live: ${platform.live_price}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography color="text.secondary">
                                        No social media platforms added yet
                                    </Typography>
                                )}
                            </ProfileSection>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
}
